/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the sae history component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { Button } from '@material-ui/core';
import { CollapsibleTable } from '@ngt/online-patient-management-ui';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import { Column } from 'react-table';
import * as Dtos from '../../api/Neon.Dtos';
import { useSaes } from '../../store/modules/saereview/saes';

/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface ISaeHistoryProps {
    patientStudyNumber?: string,
    saeNumber?: number,
    institutionCode?: string
}

interface ISaeViewButtonProps {
    patientInstitutionCode?: string,
    patientStudyNumber?: string,
    saeNumber?: number,
    label?: string
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    loading: {
        textAlign: 'center',
        padding: theme.spacing(3)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const useSaeColumns = (saes: Array<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>> | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>>> = [
            {
                Header: 'SAE No',
                accessor: sae => sae.form.saeNumber,
                minWidth: 80,
                maxWidth: 80
            },
            {
                Header: 'Onset Date',
                accessor: sae => sae.form.dateOfSAE,
                Cell: (props: any) => props.cell.value ? DateTime.fromISO(props.cell.value).toFormat('dd/MM/yyyy') : '',
                minWidth: 100,
                maxWidth: 100
            },
            {
                Header: 'Main Event',
                accessor: sae => sae.form.ctcaeTerm ?? sae.form.mainEvent,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Status',
                accessor: sae => sae.form.eventStatus,
                Cell: (props: any) => {
                    return (
                        <>
                            {props.row.original.form.followUpNumber > 0 ? 'Follow-up no.' + props.row.original.form.followUpNumber : 'Initial'} {!!props.row.original.form.eventStatus ? '(' + props.row.original.form.eventStatus + ')' : ''}
                        </>
                    );
                },
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'View',
                accessor: sae => sae.form.id,
                Cell: (props: any) => {
                    return (
                        <>
                            <SaeViewButton
                                patientInstitutionCode={props.row.original.patient.institutionCode}
                                patientStudyNumber={props.row.original.patient.studyNumber}
                                saeNumber={props.row.original.form.saeNumber}
                                label={'View'}
                            />
                        </>
                    );
                },
                minWidth: 30,
                maxWidth: 30
            },
        ];

        return cols;
    }, []);

    return columns;
};

const SaeViewButton: React.FunctionComponent<ISaeViewButtonProps> = ({
    patientInstitutionCode,
    patientStudyNumber,
    saeNumber,
    label
}) => {
    const classes = useStyles();

    const saeLink = `/sae/${patientInstitutionCode}/${patientStudyNumber}/${saeNumber}`;

    return <>
        <Button
            variant="contained"
            color="primary"
            component={Link}
            className={classes.button}
            to={saeLink}
            size="small"
        >
            {label}
        </Button>
    </>
};

const SaeHistory: React.FunctionComponent<ISaeHistoryProps> = ({
    patientStudyNumber,
    saeNumber,
    institutionCode
}) => {
    const [saes, saesLoadState, saesActions] = useSaes(
        institutionCode
    );

    React.useEffect(() => {
        saesActions.load();

        return () => {
            saesActions.clear();
        }
    }, [saesActions])

    const patientSaes = React.useMemo(() => {
        if (!saes) {
            return null;
        }

        return saes.filter(sae => sae.patient.studyNumber === patientStudyNumber && sae.form.saeNumber !== saeNumber).sort((a, b) => (a.form.saeNumber ?? 0) - (b.form.saeNumber ?? 0));
    }, [saes, patientStudyNumber, saeNumber]);

    const saeColumns = useSaeColumns(patientSaes);

    return (
        <>
            <CollapsibleTable
                title="SAE History"
                loadState={saesLoadState}
                data={patientSaes}
                columns={saeColumns}
                entityName="SAE"
                pagination={true}
                recordsPerPage={3}
                startCollapsed={true}
                PaginationProps={{
                    style: { float: 'right' }
                }}
                titleFormattingOptions={{
                    plural: false,
                    lowercase: false
                }}
                noItemsFormattingOptions={{
                    plural: false,
                    lowercase: false
                }}
                entityFormattingOptions={{
                    plural: true,
                    lowercase: false
                }}
            />
        </>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default SaeHistory;